import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { mutate } from 'swr';
import { useDebouncedCallback } from 'use-debounce';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  CreditCard,
  Loader2,
  MapPin,
  Phone,
  User,
  Share2,
} from 'lucide-react';

import axiosInstance from '@/lib/axios';
import { useUser } from '@/hooks/use-user';
import { useCommunity } from '@/hooks/use-community';
import { communityIdFromPathname } from '@/lib/community-route';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import {
  SegmentedTabs,
  SegmentedTabsContent,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import { ImageUpload } from '@/components/ui/image-upload';
import { AccountHeader } from '@/components/layouts/AccountHeader';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { FormWheelDatePickerField } from '@/components/forms/form-wheel-date-picker-field';
import { FormPhoneField } from '@/components/forms/form-phone-field';
import { FormFileUploadField } from '@/components/forms/form-file-upload-field';
import { OnboardingMembershipAutosaveForm } from '@/components/onboarding/onboarding-membership-autosave-form';
import { GivesAsksCommunitySection } from '@/components/profile/gives-asks-community-section';
import { SocialLinksEditor } from '@/components/social/social-links-editor';
import type { UserWithCommunities } from '@/types';

const profileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  middleName: z.string().optional(),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  phone: z.string().min(10, 'Phone number is invalid'),
  image: z.string().optional(),
  aadhaarCardUrl: z.string().optional().nullable(),
  dob: z.string().optional().nullable(),
  anniversaryDate: z.string().optional().nullable(),
  gender: z.string().optional().nullable(),
  bloodGroup: z.string().optional().nullable(),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().optional(),
      pincode: z.string().optional(),
      googleMapLink: z.string().url('Invalid URL').optional().or(z.literal('')),
    })
    .optional()
    .nullable(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((v) => ({
  value: v,
  label: v,
}));

function buildProfileDefaults(user: UserWithCommunities): ProfileFormValues {
  return {
    firstName: user.firstName || '',
    middleName: user.middleName || '',
    lastName: user.lastName || '',
    phone: user.phone ? (user.phone.startsWith('+') ? user.phone : `+${user.phone}`) : '',
    image: user.image || '',
    aadhaarCardUrl: user.aadhaarCardUrl || null,
    dob: user.dob || null,
    anniversaryDate: user.anniversaryDate || null,
    gender: user.gender || '',
    bloodGroup: user.bloodGroup || '',
    address: user.address || {
      line1: '',
      line2: '',
      city: '',
      state: '',
      country: '',
      pincode: '',
      googleMapLink: '',
    },
  };
}

export default function EditProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { communityId } = useParams<{ communityId?: string }>();
  const { user, isLoading: isUserLoading } = useUser();

  const communitySegment = communityId ?? communityIdFromPathname(location.pathname);

  const { community: communityResolution, isLoading: isCommunityScopeLoading } =
    useCommunity(communitySegment);

  const awaitingCommunityCanonical =
    Boolean(communitySegment) && isCommunityScopeLoading && !communityResolution?.community;

  const profilePath = communitySegment ? `/${communitySegment}/profile` : '/profile';

  if (isUserLoading || awaitingCommunityCanonical) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Please log in to edit your profile.
      </div>
    );
  }

  const additionalDetailsCommunityId = communitySegment
    ? communityResolution?.community?.id
    : undefined;

  return (
    <EditProfileShell communitySegment={communitySegment} user={user}>
      <Button
        variant="ghost"
        className="mb-2 pl-0 hover:bg-transparent hover:text-primary"
        onClick={() => navigate(profilePath)}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Profile
      </Button>

      <EditProfileForm
        user={user}
        additionalDetailsCommunityId={additionalDetailsCommunityId}
        showAadhaarCard={isAadhaarCardEnabledForUser(user, additionalDetailsCommunityId)}
      />
    </EditProfileShell>
  );
}

function EditProfileShell({
  communitySegment,
  user,
  children,
}: {
  communitySegment?: string;
  user: UserWithCommunities;
  children: React.ReactNode;
}) {
  if (!communitySegment) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AccountHeader user={user} />
        <main className="flex-1 overflow-y-auto w-full px-2 py-2 md:px-6 md:py-8">
          {/* Single width clamp; avoids stacking max-width + padding twice */}
          <div className="w-full max-w-[1600px] mx-auto">{children}</div>
        </main>
      </div>
    );
  }

  /* Inside DashboardShell: main + container already add padding/max-width — no extra constrain here */
  return <div className="w-full min-w-0 max-w-none -m-2 md:m-0">{children}</div>;
}

interface EditProfileFormProps {
  user: UserWithCommunities;
  /** Canonical community id: when URL is community-scoped, limit Additional details to this id. */
  additionalDetailsCommunityId?: string;
  showAadhaarCard: boolean;
}

function isAadhaarCardEnabledForUser(
  user: UserWithCommunities,
  additionalDetailsCommunityId?: string
): boolean {
  const communities = additionalDetailsCommunityId
    ? user.communities.filter((c) => c.id === additionalDetailsCommunityId)
    : user.communities;
  return communities.some((c) => c.profileFieldConfig?.aadhaarCard?.enabled);
}

function EditProfileForm({
  user,
  additionalDetailsCommunityId,
  showAadhaarCard,
}: EditProfileFormProps) {
  const communitiesWithOnboarding = useMemo(() => {
    let list = user.communities.filter((c) =>
      Boolean(c.onboardingForm?.steps && c.onboardingForm.steps.length > 0)
    );
    if (additionalDetailsCommunityId) {
      list = list.filter((c) => c.id === additionalDetailsCommunityId);
    }
    return list;
  }, [user.communities, additionalDetailsCommunityId]);

  const communitiesWithGivesAsks = useMemo(() => {
    let list = user.communities.filter((c) => Boolean(c.isGivesAsksEnabled));
    if (additionalDetailsCommunityId) {
      list = list.filter((c) => c.id === additionalDetailsCommunityId);
    }
    return list;
  }, [user.communities, additionalDetailsCommunityId]);

  return (
    <Card className="w-full border-0 md:border md:border-border bg-background/50 backdrop-blur-md shadow-none md:shadow-2xl">
      <SegmentedTabs defaultValue="personal" className="w-full">
        <CardHeader className="space-y-3 p-3 md:p-6">
          <CardTitle className="text-2xl">Edit Profile</CardTitle>
          <p className="text-sm text-muted-foreground">
            Personal fields and onboarding answers save automatically shortly after you change them.
          </p>
          <SegmentedTabsList variant="primary" className="mt-2 w-full max-w-md md:mt-4">
            <SegmentedTabsTrigger variant="primary" value="personal">
              Personal
            </SegmentedTabsTrigger>
            <SegmentedTabsTrigger variant="primary" value="additional">
              Additional details
            </SegmentedTabsTrigger>
          </SegmentedTabsList>
        </CardHeader>
        <CardContent className="p-3 pt-0 md:p-6 md:pt-0">
          <SegmentedTabsContent value="personal" className="mt-0 outline-none focus-visible:ring-0">
            <PersonalProfileAutosaveForm user={user} showAadhaarCard={showAadhaarCard} />
          </SegmentedTabsContent>
          <SegmentedTabsContent
            value="additional"
            className="mt-0 outline-none focus-visible:ring-0"
          >
            <div className="space-y-8">
              {communitiesWithOnboarding.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  You are not in any communities with onboarding questions. When you join one, you
                  can edit those answers here.
                </p>
              ) : (
                communitiesWithOnboarding.map((c) => (
                  <OnboardingMembershipAutosaveForm
                    key={c.id}
                    communityId={c.id}
                    communityName={c.name}
                    config={c.onboardingForm!}
                    initialAnswers={c.metadata?.onboardingAnswers}
                  />
                ))
              )}
              {communitiesWithGivesAsks.map((c) => (
                <GivesAsksCommunitySection key={c.id} communityId={c.id} communityName={c.name} />
              ))}
            </div>
          </SegmentedTabsContent>
        </CardContent>
      </SegmentedTabs>
    </Card>
  );
}

function PersonalProfileAutosaveForm({
  user,
  showAadhaarCard,
}: {
  user: UserWithCommunities;
  showAadhaarCard: boolean;
}) {
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: buildProfileDefaults(user),
  });

  const skipNextWatchRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const persistProfile = async (overrides?: Partial<ProfileFormValues>) => {
    const values = { ...form.getValues(), ...overrides };
    const parsed = profileSchema.safeParse(values);
    if (!parsed.success) return false;
    try {
      setSaving(true);
      await axiosInstance.patch('/api/user', parsed.data);
      await mutate('/api/user');
      skipNextWatchRef.current = true;
      form.reset(parsed.data, { keepValues: true, keepDirty: false });
      return true;
    } catch (error) {
      console.error(error);
      const err = error as AxiosError<{ error: string }>;
      toast.error(err.response?.data?.error || 'Failed to save profile');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const debouncedPersist = useDebouncedCallback(() => {
    if (Object.keys(form.formState.dirtyFields).length === 0) return;
    void persistProfile();
  }, 850);

  const commitDateField = () => {
    debouncedPersist.cancel();
    queueMicrotask(() => {
      void persistProfile();
    });
  };

  useEffect(() => {
    const sub = form.watch(() => {
      if (skipNextWatchRef.current) {
        skipNextWatchRef.current = false;
        return;
      }
      debouncedPersist();
    });
    return () => sub.unsubscribe();
  }, [form, debouncedPersist]);

  return (
    <div className="relative isolate">
      <div
        className="pointer-events-none absolute right-0 top-0 z-10 flex justify-end md:right-px"
        aria-live="polite"
        aria-atomic="true"
      >
        {saving ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/15 bg-background/95 px-2.5 py-1 text-[11px] font-semibold text-primary shadow-sm backdrop-blur-sm md:text-xs">
            <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
            Saving…
          </span>
        ) : null}
      </div>
      <Form {...form}>
        <form onSubmit={(e) => e.preventDefault()} className="space-y-10">
          <section className="flex flex-col items-center">
            <FormField
              control={form.control}
              name="image"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <ImageUpload
                      value={field.value}
                      disabled={saving}
                      onChange={async (url) => {
                        field.onChange(url);
                        debouncedPersist.cancel();
                        const saved = await persistProfile({ image: url });
                        if (!saved) {
                          throw new Error('Failed to save profile');
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>

          <Section icon={<User className="size-4" />} title="Personal Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <FormInputField
                control={form.control}
                name="firstName"
                label="First Name"
                placeholder="John"
                required
              />
              <FormInputField
                control={form.control}
                name="lastName"
                label="Last Name"
                placeholder="Doe"
                required
              />
              <FormInputField
                control={form.control}
                name="middleName"
                label="Middle Name"
                placeholder="Optional"
                className="sm:col-span-2"
              />
              <FormSelectField
                control={form.control}
                name="gender"
                label="Gender"
                placeholder="Select gender"
                options={GENDER_OPTIONS}
              />
              <FormSelectField
                control={form.control}
                name="bloodGroup"
                label="Blood Group"
                placeholder="Select"
                options={BLOOD_GROUP_OPTIONS}
              />
            </div>
          </Section>

          <Section icon={<Calendar className="size-4" />} title="Important Dates">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <FormWheelDatePickerField
                control={form.control}
                name="dob"
                label="Date of Birth"
                placeholder="Select date"
                onCommit={commitDateField}
              />
              <FormWheelDatePickerField
                control={form.control}
                name="anniversaryDate"
                label="Anniversary"
                placeholder="Select date"
                onCommit={commitDateField}
              />
            </div>
          </Section>

          <Section icon={<Phone className="size-4" />} title="Contact Info">
            <FormPhoneField
              control={form.control}
              name="phone"
              label="Phone Number"
              placeholder="Enter phone number"
              disabled={saving}
              required
            />
          </Section>

          <SocialLinksAutosaveSection
            initialLinks={user.socialLinks ?? null}
            onSavingChange={setSaving}
          />

          {showAadhaarCard ? (
            <Section icon={<CreditCard className="size-4" />} title="Identity Verification">
              <FormFileUploadField
                control={form.control}
                name="aadhaarCardUrl"
                label="Aadhaar Card"
                description="Upload a clear image or PDF of your Aadhaar card. This is stored securely and not shown on your public profile."
                uploadFolder="aadhaar-cards"
                onUploadingChange={(uploading) => {
                  if (uploading) debouncedPersist.cancel();
                }}
              />
            </Section>
          ) : null}

          <Section icon={<MapPin className="size-4" />} title="Residential Address">
            <div className="space-y-4">
              <FormInputField
                control={form.control}
                name="address.line1"
                label="Street Address"
                placeholder="House / Flat No., Street"
              />
              <FormInputField
                control={form.control}
                name="address.line2"
                label="Area / Landmark"
                placeholder="Optional"
              />
              <div className="grid grid-cols-2 gap-4">
                <FormInputField
                  control={form.control}
                  name="address.city"
                  label="City"
                  placeholder="City"
                />
                <FormInputField
                  control={form.control}
                  name="address.state"
                  label="State"
                  placeholder="State"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormInputField
                  control={form.control}
                  name="address.country"
                  label="Country"
                  placeholder="Country"
                />
                <FormInputField
                  control={form.control}
                  name="address.pincode"
                  label="Pincode"
                  placeholder="Pincode"
                />
              </div>
              <FormInputField
                control={form.control}
                name="address.googleMapLink"
                label="Google Maps Link"
                placeholder="https://maps.google.com/..."
              />
            </div>
          </Section>
        </form>
      </Form>
    </div>
  );
}

function SocialLinksAutosaveSection({
  initialLinks,
  onSavingChange,
}: {
  initialLinks: Record<string, string> | null;
  onSavingChange: (saving: boolean) => void;
}) {
  const [links, setLinks] = useState<Record<string, string>>(initialLinks ?? {});
  const debouncedSave = useDebouncedCallback(async (val: Record<string, string>) => {
    onSavingChange(true);
    try {
      await axiosInstance.patch('/api/user', { socialLinks: val });
      await mutate('/api/user');
    } catch (error) {
      console.error(error);
      toast.error('Failed to save social links');
    } finally {
      onSavingChange(false);
    }
  }, 850);

  return (
    <Section icon={<Share2 className="size-4" />} title="Social Media Links">
      <SocialLinksEditor
        value={links}
        onChange={(val) => {
          setLinks(val);
          debouncedSave(val);
        }}
      />
    </Section>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2 pb-2 border-b border-border/50">
        <div className="p-1.5 rounded-lg bg-primary/10 text-primary">{icon}</div>
        <h3 className="font-bold text-sm tracking-tight">{title}</h3>
      </div>
      {children}
    </section>
  );
}
